import { memo, useCallback, useState } from 'react'

import { ControlPointDuplicate } from '@mui/icons-material'
import { Divider, ListItemIcon, MenuItem, Skeleton, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { ActionMenu, AnimatedButton } from '@shared/components/ContextMenu'
import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { useProgramCreateMutation, useProgramDeleteMutation } from 'hooks/mutations/content'
import { expManifestsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/ExpManifests'
import { expManifestRoute } from 'pages/NWPlatform/Workspaces/Workspace/ExpManifests/ExpManifest'
import { expManifestQueryKeys, workspaceQueryKeys } from 'services/queryKeys'

const ActionsMenuRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()

  const { expManifestId, workspaceId } = expManifestRoute.useParams()
  const navigate = useNavigate({ from: expManifestRoute.fullPath })

  const { data: workspace } = useQuery(workspaceQueryKeys.detail({ workspaceId }))
  const expManifestQuery = useQuery(expManifestQueryKeys.detail({ programId: expManifestId }))
  const { data: expManifest } = expManifestQuery

  const { mutate: createExpManifest, ...expManifestCreateMutation } = useProgramCreateMutation()
  const { mutate: deleteExpManifest } = useProgramDeleteMutation()

  const handleDuplicateExpManifest = useCallback(() => {
    if (!expManifest || !workspace?.contentRepositoryId) return

    setAnchorEl(undefined)

    createExpManifest(
      {
        expManifestInsert: {
          repositoryId: workspace.contentRepositoryId,
          ...expManifest,
          name: `${expManifest.name} (${t('duplicate')})`,
        },
      },
      {
        onSuccess: () => {
          enqueueSnackbar(t('duplicationSuccess'), { variant: 'success' })
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [createExpManifest, enqueueSnackbar, expManifest, t, workspace?.contentRepositoryId])

  const handleDeletion = useCallback(() => {
    if (!expManifest) return

    setAnchorEl(undefined)

    deleteExpManifest(
      { programId: expManifestId },
      {
        onSuccess: () => {
          enqueueSnackbar(t('domain:Workspace.ExpManifest.nameDelete', { name: expManifest.name }), {
            variant: 'success',
          })
          setTimeout(() => {
            navigate({ to: expManifestsOverviewRoute.to, replace: true })
          }, 300)
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [deleteExpManifest, enqueueSnackbar, expManifest, expManifestId, navigate, t])

  return (
    <>
      <AnimatedButton
        open={!!anchorEl}
        IconButtonProps={{
          onClick: (e) => setAnchorEl(e.currentTarget),
          color: 'inherit',
        }}
      />
      <ActionMenu
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(undefined)
        }}
        title={expManifest?.name}
        entity={t('entity:content.expManifest', { count: 1 })}
      >
        <MenuItem onClick={handleDuplicateExpManifest} disabled={expManifestCreateMutation.isPending}>
          <ListItemIcon>
            <ControlPointDuplicate />
          </ListItemIcon>
          <Typography sx={{ width: '100%' }}>
            {expManifestCreateMutation.isPending ? <Skeleton variant="text" /> : t('duplicate')}
          </Typography>
        </MenuItem>

        <MenuItemCopyId onSuccess={() => setAnchorEl(undefined)} id={expManifestId} />

        <Divider sx={{ my: 1 }} component="li" />

        <MenuItemDelete
          staticLabel={t('domain:Workspace.ExpManifest.deleteExpManifest', { count: 1 })}
          onConfirm={handleDeletion}
        />
      </ActionMenu>
    </>
  )
}

export const ActionsMenu = memo(ActionsMenuRaw)
